/**
 * The brand the sign-in page is dressed in (anubis, 2026-10-05): painted from this origin's cache on
 * the first frame, then refreshed from `brand`'s open `GET /v1/public` — the route every public page
 * reads. It replaced this app's own `GET /v1/config` call for the page's language and line: the open
 * route carries both (`default_language`, `tagline`) and, since the same day, `login_video_url`.
 *
 * No React Query here: the page makes one request, once, and nothing else on it is cached.
 */
import { useEffect, useState } from 'react';
import {
  fetchPublicBrand,
  readPublicBrandCache,
  themeSource,
  writePublicBrandCache,
  type PublicBrandTheme,
} from '@martincastroalvarez/brand-public';

const STORAGE_KEY = 'login.brand';

export type Brand = PublicBrandTheme;

let pending: Promise<Brand | null> | null = null;

function load(): Promise<Brand | null> {
  pending ??= fetchPublicBrand()
    .then((answer) => {
      writePublicBrandCache(answer, { storageKey: STORAGE_KEY });
      return themeSource(answer);
    })
    .catch(() => null);
  return pending;
}

export function useBrand(): Brand | null {
  return useBrandState().brand;
}

/**
 * The brand and whether it has SETTLED: `true` once it came from this origin's cache or the
 * request has finished (answered or not). A brand that never arrives still settles, so nothing
 * waiting on it waits forever.
 */
export function useBrandState(): { brand: Brand | null; settled: boolean } {
  const [brand, setBrand] = useState<Brand | null>(() => {
    const cached = readPublicBrandCache({ storageKey: STORAGE_KEY });
    return cached ? themeSource(cached) : null;
  });
  const [settled, setSettled] = useState<boolean>(() => brand !== null);
  useEffect(() => {
    let live = true;
    void load().then(
      (fresh) => {
        if (!live) return;
        if (fresh) setBrand(fresh);
        setSettled(true);
      },
      () => live && setSettled(true),
    );
    return () => {
      live = false;
    };
  }, []);
  return { brand, settled };
}

/** The fleet's own footage, played when the brand has not picked a login video (martin's case). */
export const FALLBACK_VIDEO = '/background.mp4';
export const FALLBACK_POSTER = '/background-poster.jpg';

export interface LoginVideo {
  video: string | null;
  poster: string | null;
}

/**
 * The clip behind the welcome, decided only once the live brand has answered (2026-10-10).
 *
 * `null` until then, so the stage shows its plain wash rather than the fleet's footage or a cached
 * brand's old clip, either of which would be swapped out a moment later. Only an answer with no
 * `login_video_url` gets the fallback; a failed request plays what the cache last knew, else the
 * fallback.
 */
export function useLoginVideo(): LoginVideo {
  const [chosen, setChosen] = useState<LoginVideo>({ video: null, poster: null });
  useEffect(() => {
    let live = true;
    void load().then((fresh) => {
      if (!live) return;
      const cached = readPublicBrandCache({ storageKey: STORAGE_KEY });
      const known = fresh ?? (cached ? themeSource(cached) : null);
      const url = String(known?.login_video_url ?? '').trim();
      setChosen(
        url ? { video: url, poster: null } : { video: FALLBACK_VIDEO, poster: FALLBACK_POSTER },
      );
    });
    return () => {
      live = false;
    };
  }, []);
  return chosen;
}
